<template>
  <article class="review">
    <header class="review__header">
      <div class="review__author">
        <h3 class="review__item-meta">
          {{ review.authorName }}
        </h3>
        <time
          class="review__item-meta review__date text-disabled text-small"
          :datetime="new Date(`${review.date} GMT`).toISOString()"
        >
          {{ translateDate(review.date) }}
        </time>
      </div>

      <ReviewRate
        :rate="review.rate"
        :likes="review.likes"
        :dislikes="review.dislikes"
      />
    </header>

    <p class="review__text">
      {{ review.text }}
      <span v-if="review.isEdited" class="text-small text-disabled">
        ({{ $t('EDITED') }})
      </span>
    </p>

    <div class="review__info text-gray">
      <small class="review__item-meta">
        <span class="text-bidi-embed">
          {{ $t('APP.REVIEWS.API_LEVEL') }}
        </span>
        :
        <strong>{{ review.apiLevel }}</strong>
      </small>
      <small class="review__item-meta">
        {{ $t('APP.REVIEWS.APP_VERSION') }}:
        <strong>{{ review.versionCode }} ({{ review.versionName }})</strong>
      </small>

      <small class="review__item-meta">
        {{ $t('APP.REVIEWS.STATUS') }}:
        <strong>{{ reviewStatusText }}</strong>
      </small>
    </div>

    <ReviewReply
      :review="review"
      :show-reply-box="showReplyBox"
      @cancel-reply="toggleShowReplyBox"
    />

    <footer class="review__footer">
      <div class="review__controls">
        <span v-if="showReviewControls" class="review__item-meta">
          <ElButton
            v-if="isReported"
            type="danger"
            size="small"
            :plain="true"
            @click.native="cancelReviewReport"
          >
            {{ $t('APP.REVIEWS.CANCEL_REPORT') }}
          </ElButton>

          <ElButton
            v-else
            type="danger"
            size="small"
            :plain="true"
            @click.native="showReviewReportModal"
          >
            {{ $t('APP.REVIEWS.REPORT') }}
          </ElButton>

          <ElButton
            v-show="!showReplyBox && !isReplyDisabled"
            size="small"
            :plain="true"
            @click.native="toggleShowReplyBox"
          >
            {{ $t('APP.REVIEWS.REPLY') }}
          </ElButton>
        </span>
      </div>
    </footer>
    <ReviewReportModal
      v-model="showReportModal"
      :review-text="review.text"
      @confirm="reportReview"
    />
  </article>
</template>

<script>
import Reviews from '@/api/app-reviews';
import ReviewReply from './ReviewReply';
import ReviewRate from './ReviewRate';
import { dateFormat } from '@/utils/date';
import packageName from '@/mixins/packageName';
import lang from '@/mixins/lang';
import modal from '@/mixins/modal';
import ReviewReportModal from './ReviewReportModal';

export default {
  name: 'Review',

  components: {
    ReviewReply,
    ReviewRate,
    ReviewReportModal,
  },

  mixins: [packageName, lang, modal],

  props: {
    review: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isReported: this.review.isReported,
      showReplyBox: false,
      showReportModal: false,
    };
  },

  computed: {
    showReviewControls() {
      return 'PNGLA'.includes(this.review.state);
    },
    isReplyDisabled() {
      return !'ALRPFBG'.includes(this.review.state);
    },
    reviewStatusText() {
      const { state } = this.review;

      const stateTexts = {
        H: this.$t('APP.REVIEWS.STATUS.INVALID'),
        D: this.$t('APP.REVIEWS.STATUS.INVALID'),
        M: this.$t('APP.REVIEWS.STATUS.INVALID'),
        C: this.$t('APP.REVIEWS.STATUS.PENDING'),
        W: this.$t('APP.REVIEWS.STATUS.PENDING'),
        P: this.$t('APP.REVIEWS.STATUS.PUBLISHED'),
        G: this.$t('APP.REVIEWS.STATUS.PUBLISHED'),
        L: this.$t('APP.REVIEWS.STATUS.PUBLISHED'),
        A: this.$t('APP.REVIEWS.STATUS.PUBLISHED'),
      };

      return stateTexts[state];
    },
  },

  methods: {
    translateDate(date, timeFormat = ' HH:mm') {
      let toFormat = `DD/MM/YYYY${timeFormat}`;

      if (this.lang === 'fa') {
        toFormat = `jYYYY/jMM/jDD${timeFormat}`;
      }

      return dateFormat(`YYYY/M/D${timeFormat}`)(toFormat)(date);
    },

    reportReview() {
      this.closeModal('showReportModal');
      return Reviews.reviewReport({
        packageName: this.packageName,
        id: this.review.id,
      })
        .then(message => {
          this.isReported = true;
          this.$message.success(message);
        })
        .catch(({ message }) => {
          this.$message.error(message);
        });
    },

    cancelReviewReport() {
      return Reviews.cancelReviewReport({
        packageName: this.packageName,
        id: this.review.id,
      })
        .then(message => {
          this.isReported = false;
          this.$message.success(message);
        })
        .catch(({ message }) => {
          this.$message.error(message);
        });
    },

    showReviewReportModal() {
      this.openModal('showReportModal');
    },

    toggleShowReplyBox() {
      this.showReplyBox = !this.showReplyBox;
    },
  },
};
</script>

<style lang="scss">
.review {
  &:hover {
    background-color: $table-row-hover;
  }

  & + & {
    border-top: 1px solid $lb-lighter-gray;
  }

  &__header,
  &__footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    @include breakpoint(medium) {
      flex-wrap: nowrap;
    }
  }

  &__text {
    overflow-wrap: break-word;
  }

  &__item-meta {
    & + & {
      margin-left: $global-margin / 4;

      @include rtl() {
        margin-left: 0;
        margin-right: $global-margin / 4;
      }

      @include breakpoint(medium) {
        margin-left: $global-margin;

        @include rtl() {
          margin-right: $global-margin;
        }
      }
    }
  }

  &__header {
    margin-bottom: $global-margin;

    h3 {
      display: inline-block;
      margin-bottom: 0;
      font-size: $lb-global-font-size;
    }
  }

  &__footer {
    direction: ltr;
  }

  &__author {
    display: flex;
    flex-direction: column;

    @include breakpoint(medium) {
      display: block;
    }
  }

  &__date {
    flex-grow: 1;
    font-weight: normal;

    @include breakpoint(medium) {
      flex-grow: 0;
    }
  }

  &__controls {
    display: flex;
    flex-basis: 100%;
    align-items: center;
    justify-content: flex-start;

    font-weight: normal;

    @include breakpoint(medium) {
      display: block;
      flex-basis: auto;
    }
  }
}
</style>
